// Monitoring-Tab "Gemeldet": GET /monitoring/reported — offene + kuerzlich geloeste Meldungen.
// Zeile aufklappbar -> Kommentarverlauf (getReportComments); "Loesen"-Dialog nur fuer Admins
// (resolveReport, Pflichtkommentar). Feldnamen laut Bauplan-Prosa: documentId, fileName,
// supplierId, status, reportedAt, reportedBy, resolved, resolvedAt, resolvedBy, commentCount.

import React, { useCallback, useEffect, useState } from 'react';
import { Chip, StatusBadge, fmtDateTime, inputCls, btnPrimary, btnGhost } from '../ui';
import { getMonitoring, getReportComments, resolveReport } from '../../api';

const AbMonitoringReportedTab = ({ user, isDarkMode, t, isAdmin, onOpenDocument, showSuccess, showError }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null); // documentId mit offenem Verlauf
  const [threads, setThreads] = useState({});
  const [resolveTarget, setResolveTarget] = useState(null); // Zeile im Loesen-Dialog
  const [resolveComment, setResolveComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    // "Gemeldet" bleibt bewusst unpaginiert im 50er-Standard (Bauplan nennt keine eigene Seitengroesse,
    // dieselbe Konvention wie die uebrigen Tabs).
    getMonitoring(user, 'reported', { page: 1, pageSize: 50 })
      .then((res) => { setItems(Array.isArray(res?.items) ? res.items : []); setError(null); })
      .catch((e) => { setError(e.message); showError?.(e.message); })
      .finally(() => setLoading(false));
  }, [user, showError]);

  useEffect(() => { load(); }, [load]);

  const toggleThread = async (documentId) => {
    if (expanded === documentId) { setExpanded(null); return; }
    setExpanded(documentId);
    if (!threads[documentId]) {
      try {
        const c = await getReportComments(user, documentId);
        setThreads((m) => ({ ...m, [documentId]: Array.isArray(c) ? c : [] }));
      } catch (e) { showError?.(e.message); }
    }
  };

  const submitResolve = async () => {
    if (!resolveTarget) return;
    if (!resolveComment.trim()) { showError?.(t('ab.shell.monResolveCommentRequired')); return; }
    setBusy(true);
    try {
      await resolveReport(user, resolveTarget.documentId, resolveComment.trim());
      showSuccess?.(t('ab.shell.monResolvedMsg'));
      setResolveTarget(null);
      setResolveComment('');
      load();
    } catch (e) {
      showError?.(e.message);
    } finally {
      setBusy(false);
    }
  };

  const th = `text-left text-[11px] uppercase tracking-wide font-semibold px-3 py-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`;
  const td = `px-3 py-2.5 text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`;

  if (loading) return <p className={`text-sm py-8 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.loading')}</p>;
  if (error && items.length === 0) return <p className={`text-sm py-6 text-center ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.shell.monLoadError')}: {error}</p>;
  if (items.length === 0) return <p className={`text-sm py-8 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.shell.monEmpty')}</p>;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className={isDarkMode ? 'border-b border-white/10' : 'border-b border-slate-200'}>
              <th className={th}>{t('ab.shell.monColReported')}</th>
              <th className={th}>{t('ab.colFile')}</th>
              <th className={th}>{t('ab.colSupplier')}</th>
              <th className={th}>{t('ab.colStatus')}</th>
              <th className={th}>{t('ab.shell.monColReportedBy')}</th>
              <th className={th}>{t('ab.shell.monColReportStatus')}</th>
              <th className={th} />
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <React.Fragment key={r.documentId}>
                <tr className={isDarkMode ? 'border-b border-white/5' : 'border-b border-slate-100'}>
                  <td className={td}>{fmtDateTime(r.reportedAt)}</td>
                  <td className={`${td} max-w-[200px] truncate`} title={r.fileName || ''}>
                    <button type="button" onClick={() => onOpenDocument(r.documentId)} className={isDarkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700'}>
                      {r.fileName || t('ab.shell.monOpen')}
                    </button>
                  </td>
                  <td className={td}>{r.supplierId || t('ab.supplierUnknown')}</td>
                  <td className={td}><StatusBadge status={r.status} isDarkMode={isDarkMode} t={t} /></td>
                  <td className={`${td} max-w-[160px] truncate`} title={r.reportedBy || ''}>{r.reportedBy || '—'}</td>
                  <td className={td}>
                    <Chip tone={r.resolved ? 'green' : 'amber'} isDarkMode={isDarkMode}>
                      {r.resolved ? t('ab.shell.myReportsDone') : t('ab.shell.myReportsOpen')}
                    </Chip>
                  </td>
                  <td className={td}>
                    <button type="button" onClick={() => toggleThread(r.documentId)} className={isDarkMode ? 'text-white/60 hover:text-white text-xs' : 'text-slate-500 hover:text-slate-900 text-xs'}>
                      {expanded === r.documentId ? `▲ ${t('ab.shell.myReportsHistory')}` : `▾ ${t('ab.shell.myReportsHistory')}`}
                    </button>
                  </td>
                  <td className={td}>
                    {isAdmin && !r.resolved && (
                      <button type="button" onClick={() => { setResolveTarget(r); setResolveComment(''); }} className={btnGhost(isDarkMode)}>
                        {t('ab.shell.monResolve')}
                      </button>
                    )}
                  </td>
                </tr>
                {expanded === r.documentId && (
                  <tr>
                    <td colSpan={8} className={`px-3 pb-3 ${isDarkMode ? 'bg-white/[0.02]' : 'bg-slate-50'}`}>
                      {!threads[r.documentId] ? (
                        <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.loading')}</p>
                      ) : threads[r.documentId].length === 0 ? (
                        <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.shell.myReportsNoComments')}</p>
                      ) : (
                        <ul className="space-y-1.5 pt-2">
                          {threads[r.documentId].map((c, i) => (
                            <li key={i} className={`text-xs ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                              <span className={isDarkMode ? 'text-white/40' : 'text-slate-400'}>
                                {fmtDateTime(c.createdAt)} · {c.author} · {t(`ab.shell.commentKind.${c.kind}`)}:
                              </span>{' '}{c.comment}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {resolveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className={`w-full max-w-md rounded-2xl p-6 ${isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-slate-200 shadow-xl'}`}>
            <h3 className={`text-base font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('ab.shell.monResolveDialogTitle')}</h3>
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{resolveTarget.fileName}</p>
            <textarea value={resolveComment} onChange={(e) => setResolveComment(e.target.value)} rows={3}
              placeholder={t('ab.shell.monResolveCommentPlaceholder')} className={`${inputCls(isDarkMode)} w-full resize-none`} />
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" className={btnGhost(isDarkMode)} onClick={() => setResolveTarget(null)} disabled={busy}>{t('common.cancel')}</button>
              <button type="button" className={btnPrimary(isDarkMode)} onClick={submitResolve} disabled={busy}>
                {busy ? t('common.saving') : t('ab.shell.monResolve')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbMonitoringReportedTab;
